/* DealRhythmPanel.jsx — Phase 29 per-deal momentum panel.
 *
 * Mounted on Deal Room intelligence rail after DealScenarioPanel.
 * Surfaces: momentum, velocity, stagnation, completion decay + audit
 * trail of source events.
 *
 * Auto-hides when rhythm.active=false.
 */
import React, { useCallback, useEffect, useState } from "react";
import { GaugeCircle, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { api } from "../../lib/api";

const POLL_MS = 120_000;

const BAND_TONE = {
  Accelerating: { bg: "rgba(16,185,129,0.10)", fg: "#047857" },
  Steady:       { bg: "rgba(99,102,241,0.08)", fg: "#4338CA" },
  Slowing:      { bg: "rgba(245,158,11,0.10)", fg: "#92400E" },
  Stalled:      { bg: "rgba(244,63,94,0.10)",  fg: "#9F1239" },
  Fast:         { bg: "rgba(16,185,129,0.10)", fg: "#047857" },
  Slow:         { bg: "rgba(245,158,11,0.10)", fg: "#92400E" },
  Severe:       { bg: "rgba(244,63,94,0.10)",  fg: "#9F1239" },
  Elevated:     { bg: "rgba(245,158,11,0.10)", fg: "#92400E" },
  Watching:     { bg: "rgba(99,102,241,0.08)", fg: "#4338CA" },
  "On track":   { bg: "rgba(16,185,129,0.10)", fg: "#047857" },
  "Mild decay": { bg: "rgba(99,102,241,0.08)", fg: "#4338CA" },
  "Notable decay": { bg: "rgba(245,158,11,0.10)", fg: "#92400E" },
  "Severe decay":  { bg: "rgba(244,63,94,0.10)",  fg: "#9F1239" },
};

const DirectionIcon = ({ direction }) => {
  if (direction === "improving") return <TrendingUp size={11} className="text-emerald-700" />;
  if (direction === "deteriorating") return <TrendingDown size={11} className="text-rose-700" />;
  return <Minus size={11} className="text-slate-500" />;
};

const Pill = ({ band, score, testId }) => {
  const tone = BAND_TONE[band] || BAND_TONE.Steady;
  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
            style={{ background: tone.bg, color: tone.fg }}>
        {band}
      </span>
      {score != null ? (
        <span className="text-[11px] font-bold tabular-nums px-2 py-0.5 rounded-md"
              style={{ background: "rgba(99,102,241,0.06)", color: "#4338CA" }}>
          {score}/100
        </span>
      ) : null}
    </div>
  );
};

const Contribution = ({ c }) => (
  <li className="flex items-start gap-2 py-1"
      data-testid={`rhythm-signal-${(c.signal || "").replace(/_/g, "-")}`}>
    <span className="inline-block w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
          style={{ background: c.weight_pts >= 0 ? "#10B981" : "#F43F5E" }} />
    <div className="min-w-0">
      <p className="text-[11.5px] text-slate-800 leading-snug">
        <strong className="text-slate-900">
          {c.weight_pts >= 0 ? "+" : ""}{c.weight_pts} pts
        </strong>{" · "}
        {c.narrative}
      </p>
    </div>
  </li>
);

export function DealRhythmPanel({ dealId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!dealId) return;
    try {
      const res = await api.get(`/api/timeline-intel/deals/${dealId}`);
      setData(res.data?.rhythm || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { refetch(); }, [refetch]);
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") refetch(); };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [refetch]);

  if (loading || !data || !data.active) return null;
  const m = data.momentum || {};
  const v = data.velocity || {};
  const s = data.stagnation || {};
  const dc = data.completion_decay || {};
  const ev = data.evidence || {};
  const contribs = m.contributions || [];
  return (
    <section className="gp-card overflow-hidden"
             data-testid="dealroom-rhythm-panel">
      <header className="p-4 sm:p-5">
        <div className="flex items-center gap-1.5">
          <GaugeCircle size={11} className="text-violet-600" strokeWidth={2.3} />
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700">
            Operational Rhythm
          </p>
        </div>
        <h2 className="text-[15px] sm:text-[16px] font-semibold text-slate-900 mt-1 leading-tight"
            data-testid="dealroom-rhythm-narrative">
          {data.narrative}
        </h2>
        <div className="flex items-center flex-wrap gap-1.5 mt-2">
          <div className="flex items-center gap-1">
            <DirectionIcon direction={m.direction} />
            <Pill band={m.band} score={m.score}
                  testId="rhythm-momentum-pill" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="rounded-md p-2"
               style={{ background: "rgba(99,102,241,0.04)",
                        border: "1px solid var(--gp-card-border)" }}
               data-testid="rhythm-velocity-block">
            <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">
              Velocity
            </p>
            <p className="text-[16px] font-bold tabular-nums text-slate-900 mt-0.5">
              {v.score}<span className="text-[10px] text-slate-500">/100</span>
            </p>
            <p className="text-[10px] text-slate-500">{v.band}</p>
          </div>
          <div className="rounded-md p-2"
               style={{ background: "rgba(99,102,241,0.04)",
                        border: "1px solid var(--gp-card-border)" }}
               data-testid="rhythm-stagnation-block">
            <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">
              Stagnation
            </p>
            <p className="text-[16px] font-bold tabular-nums text-slate-900 mt-0.5">
              {s.severity ?? 0}<span className="text-[10px] text-slate-500">/100</span>
            </p>
            <p className="text-[10px] text-slate-500">{s.band || "—"}</p>
          </div>
          <div className="rounded-md p-2"
               style={{ background: "rgba(99,102,241,0.04)",
                        border: "1px solid var(--gp-card-border)" }}
               data-testid="rhythm-decay-block">
            <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">
              Decay
            </p>
            <p className="text-[16px] font-bold tabular-nums text-slate-900 mt-0.5">
              {dc.decay_pts ?? 0}<span className="text-[10px] text-slate-500">/100</span>
            </p>
            <p className="text-[10px] text-slate-500">{dc.band}</p>
          </div>
        </div>
      </header>
      {contribs.length ? (
        <div className="px-4 sm:px-5 pb-3 pt-2 border-t"
             style={{ borderColor: "var(--gp-card-border)" }}>
          <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold"
             style={{ color: "var(--gp-violet, #7C3AED)" }}>
            Source events
          </p>
          <ul className="mt-1" data-testid="rhythm-source-events">
            {contribs.map((c) => <Contribution key={c.signal} c={c} />)}
          </ul>
        </div>
      ) : null}
      <div className="px-4 sm:px-5 pb-4 pt-2 border-t"
           style={{ borderColor: "var(--gp-card-border)" }}
           data-testid="rhythm-evidence">
        <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold"
           style={{ color: "var(--gp-violet, #7C3AED)" }}>
          Timing evidence
        </p>
        <ul className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-700">
          <li>14d activities · <strong>{ev.recent_activities_14d ?? 0}</strong></li>
          <li>15-30d activities · <strong>{ev.older_activities_15_30d ?? 0}</strong></li>
          <li>30d stage changes · <strong>{ev.stage_changes_30d ?? 0}</strong></li>
          <li>Days since last activity · <strong>{ev.days_since_last_activity ?? "—"}</strong></li>
          {ev.days_since_last_intervention != null ? (
            <li>Last intervention · <strong>{ev.days_since_last_intervention}d ago</strong></li>
          ) : null}
          {ev.silent_partners != null ? (
            <li>Silent partners · <strong>{ev.silent_partners}</strong></li>
          ) : null}
        </ul>
      </div>
    </section>
  );
}

export default DealRhythmPanel;
